import React, { useEffect, useState, useCallback, useRef } from "react";
import { Timer, CircleAlert, CheckCircle, XCircle, AlertTriangle, Monitor, Camera, Share, ArrowUpRight } from "lucide-react";
import { VIDEO_CALL_END_REASONS } from "@/constants/video-call";
import { TimelineEventService, TimelineEventResponse } from "@/services/video-call/timelineEventService";
import { Lead } from "@/types/video-call";
import { StarRating } from "@/components/ui/StarRating";
import { FeedbackService } from "@/services/video-call/feedbackService";
import type { InterviewFeedbackInput } from "@/types/video-call/feedback";

interface InterviewCompleteData {
  userName: string;
  totalTime: string;
  violationsCount: number;
  endReason?: string;
  violations?: Array<{
    type: string;
    count: number;
  }>;
  detailedViolations?: TimelineEventResponse | null;
}

export const InterviewComplete: React.FC = () => {
  // Change page title to "Hirevox" for interview complete page
  useEffect(() => {
    const originalTitle = document.title;
    document.title = "Hirevox";

    return () => {
      document.title = originalTitle;
    };
  }, []);

  const [interviewData, setInterviewData] = useState<InterviewCompleteData>({
    userName: "",
    totalTime: "0 min",
    violationsCount: 0,
    endReason: VIDEO_CALL_END_REASONS.INTERVIEW_COMPLETED,
    violations: [],
    detailedViolations: null,
  });
  const [isLoadingViolations, setIsLoadingViolations] = useState(false);
  const [violationsError, setViolationsError] = useState<string | null>(null);

  // Feedback state
  const [rating, setRating] = useState<number>(0);
  const [feedbackText, setFeedbackText] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasSubmittedFeedback, setHasSubmittedFeedback] = useState(false);
  const [showExitWarning, setShowExitWarning] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [feedbackTextError, setFeedbackTextError] = useState<string | null>(null);

  const exitAttemptRef = useRef(false);
  const NETWORK_ERROR_MESSAGE =
    "Unable to submit feedback. Please check your internet connection and try again.";
  const FEEDBACK_REQUIRED_MESSAGE = "Feedback is required";
  const isFeedbackTextRequired = rating > 0 && rating <= 3;
  const hasValidFeedbackText = feedbackText.trim().length > 0;

  const handleExitAttempt = useCallback(() => {
    exitAttemptRef.current = false;
    if (!hasSubmittedFeedback) {
      setShowExitWarning(true);
    }
  }, [hasSubmittedFeedback]);

  const formatTotalTime = (time: string) => {
    const parts = time.split(':');
    if (parts.length === 2) {
      return `${parts[0]} min ${parts[1]} sec`;
    }
    return time;
  };
  
  // Manual testing states
  const [testCallId, setTestCallId] = useState("");

  // Helper function to deduplicate timeline events for display
  const deduplicateTimelineEvents = useCallback((events: any[]): any[] => {
    const seenEvents = new Map<string, any>();
    
    return events.filter(event => {
      // Create a unique key for deduplication
      let eventKey: string;
      
      if (event.type === 'screen_share_violation') {
        // For screen share violations, use type + violation type + termination reason + time window (1 second)
        const timeWindow = Math.floor(new Date(event.timestamp).getTime() / 1000);
        eventKey = `${event.type}_${event.data?.violationType}_${event.data?.terminationReason}_${timeWindow}`;
      } else if (event.type === 'window_blur' || event.type === 'tab_change') {
        // For window/tab events, use type + action + time window (1 second)
        const timeWindow = Math.floor(new Date(event.timestamp).getTime() / 1000);
        eventKey = `${event.type}_${event.action}_${timeWindow}`;
      } else {
        // For other events, use type + action + time window
        const timeWindow = Math.floor(new Date(event.timestamp).getTime() / 1000);
        eventKey = `${event.type}_${event.action}_${timeWindow}`;
      }
      
      // Check if we've seen this event already
      if (seenEvents.has(eventKey)) {
        return false; // Filter out duplicate
      }
      
      // Mark this event as seen
      seenEvents.set(eventKey, event);
      return true; // Keep this event
    });
  }, []);

  useEffect(() => {
    // Get data from URL parameters
    const searchParams = new URLSearchParams(window.location.search);
    const duration = searchParams.get('duration') || "0 min";
    const violations = parseInt(searchParams.get('violations') || "0", 10);
    const userName = searchParams.get('userName') || "";
    const endReason = searchParams.get('endReason') || VIDEO_CALL_END_REASONS.INTERVIEW_COMPLETED;
    const callId = searchParams.get('callId');

    // Extract URL parameters
    
    // Function to fetch timeline violations
    const fetchTimelineViolations = async (callId: string) => {
      try {
        setIsLoadingViolations(true);
        setViolationsError(null);

        const timelineResponse = await TimelineEventService.getTimelineEvents(callId, 'violations');
        
        return timelineResponse;
      } catch (error) {
        console.error('🔍 [InterviewComplete] TIMELINE API ERROR:');
        console.error('  - Error details:', error);
        console.error('  - Error message:', error instanceof Error ? error.message : String(error));
        setViolationsError('Failed to load detailed violation data');
        return null;
      } finally {
        setIsLoadingViolations(false);
      }
    };

    // Also try to get data from session storage as fallback
    const sessionData = sessionStorage.getItem('interviewComplete');
    
    // Check session storage for interview data
    
    // Check for violation data in session storage too
    const violationData = sessionStorage.getItem('interviewViolationData');
    
    const processData = async () => {
      let finalData: InterviewCompleteData;

      // Debug logging for end reason tracking
      console.log('🔍 [InterviewComplete] Processing interview completion data:', {
        endReason,
        duration,
        violations,
        sessionDataExists: !!sessionData,
      });

      if (sessionData) {
        try {
          const parsedData = JSON.parse(sessionData);
          finalData = {
            userName: parsedData.userName || userName || "Candidate",
            totalTime: parsedData.totalTime || duration,
            violationsCount: parsedData.violationsCount || violations,
            endReason: parsedData.endReason || endReason,
            violations: parsedData.violations || [],
            detailedViolations: null,
          };
          // Clear session storage after use
          sessionStorage.removeItem('interviewComplete');

          console.log('🔍 [InterviewComplete] Using session data:', {
            endReason: finalData.endReason,
            source: 'sessionStorage'
          });
        } catch (error) {
          console.error('Error parsing session data:', error);
          finalData = {
            userName: userName || "Candidate",
            totalTime: duration,
            violationsCount: violations,
            endReason: endReason,
            violations: [],
            detailedViolations: null,
          };

          console.log('🔍 [InterviewComplete] Using URL params due to session error:', {
            endReason: finalData.endReason,
            source: 'urlParams'
          });
        }
      } else {
        finalData = {
          userName: userName || "Candidate",
          totalTime: duration,
          violationsCount: violations,
          endReason: endReason,
          violations: [],
          detailedViolations: null,
        };

        console.log('🔍 [InterviewComplete] Using URL params (no session data):', {
          endReason: finalData.endReason,
          source: 'urlParams'
        });
      }

      // Set initial data
      setInterviewData(finalData);

      // Fetch detailed violations if we have callId
      // Skip API call for fallback call IDs (they won't exist in the database)
      if (callId && !callId.startsWith('fallback-')) {
        
        const timelineViolations = await fetchTimelineViolations(callId);
        if (timelineViolations) {
          // First deduplicate the timeline events
          const deduplicatedEvents = deduplicateTimelineEvents(timelineViolations.timeline_events);
          
          // Calculate actual violations by filtering deduplicated timeline events
          const actualViolationCount = deduplicatedEvents.filter(event => {
            // Window blur: only 'blur' action is a violation
            if (event.type === 'window_blur') {
              return event.action === 'blur';
            }
            // Tab change: only 'hidden' action is a violation
            if (event.type === 'tab_change') {
              return event.action === 'hidden';
            }
            // Window manipulation: 'resize', 'maximize', and 'restore' are violations
            if (event.type === 'window_manipulation') {
              return event.action === 'resize' || event.action === 'maximize' || event.action === 'restore' || event.action === 'violation' || event.action === 'warning' || event.action === 'penalty';
            }
            // Screen share violations: always count as violations
            if (event.type === 'screen_share_violation') {
              return event.action === 'violation' || event.action === 'warning' || event.action === 'penalty';
            }
            // For other types, consider violation/warning/penalty actions as violations
            return event.action === 'violation' || event.action === 'warning' || event.action === 'penalty';
          }).length;

          setInterviewData(prev => ({
            ...prev,
            detailedViolations: {
              ...timelineViolations,
              timeline_events: deduplicatedEvents // Use deduplicated events
            },
            // Use the corrected violation count
            violationsCount: actualViolationCount,
          }));
        }
      } else if (callId && callId.startsWith('fallback-')) {
        console.warn('🔍 [InterviewComplete] FALLBACK CALL ID DETECTED:');
        console.warn('  - fallback callId:', callId);
        console.warn('  - skipping API call for detailed violations');
      } else {
        console.warn('🔍 [InterviewComplete] No callId: cannot fetch detailed violations');
      }
    };

    processData();
  }, []);

  // Exit prevention logic - warn user if they haven't submitted feedback
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (!hasSubmittedFeedback) {
        exitAttemptRef.current = true;
        e.preventDefault();
        // Modern browsers require returnValue to be set
        e.returnValue = "";
        return "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [hasSubmittedFeedback]);

  useEffect(() => {
    const maybeShowOverlay = () => {
      if (exitAttemptRef.current && !hasSubmittedFeedback) {
        handleExitAttempt();
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        maybeShowOverlay();
      }
    };

    window.addEventListener("focus", maybeShowOverlay);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("focus", maybeShowOverlay);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [hasSubmittedFeedback, handleExitAttempt]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const isCloseShortcut =
        (event.key === "w" && (event.metaKey || event.ctrlKey)) ||
        (event.key === "F4" && event.altKey);

      if (isCloseShortcut && !hasSubmittedFeedback) {
        event.preventDefault();
        handleExitAttempt();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [hasSubmittedFeedback, handleExitAttempt]);

  useEffect(() => {
    if (hasSubmittedFeedback) {
      setShowExitWarning(false);
    }
  }, [hasSubmittedFeedback]);

  // Feedback submission handler
  const handleSubmitFeedback = async () => {
    if (rating === 0) {
      console.warn("⚠️ Cannot submit feedback without a rating");
      return;
    }

    if (isFeedbackTextRequired && !hasValidFeedbackText) {
      setFeedbackTextError(FEEDBACK_REQUIRED_MESSAGE);
      return;
    }

    try {
      setIsSubmitting(true);
      setSubmitError(null);
      setFeedbackTextError(null);

      // Get callId from URL
      const searchParams = new URLSearchParams(window.location.search);
      const callId = searchParams.get("callId");
      const organizationId = searchParams.get("organizationId");

      // Validate callId
      if (!callId || callId.startsWith("fallback-")) {
        console.error("[Feedback] Invalid or fallback callId:", callId);
        setSubmitError("Cannot submit feedback: Invalid session ID");
        return;
      }

      if (!organizationId) {
        console.error("[Feedback] Missing organizationId for call:", callId);
        setSubmitError("Cannot submit feedback: Missing organization context");
        return;
      }

      // Prepare feedback data
      const feedbackInput: InterviewFeedbackInput = {
        rating,
        feedback_text: feedbackText.trim() || undefined,
      };

      console.log("📝 [Feedback] Submitting feedback for call:", callId);

      // Submit to database
      const result = await FeedbackService.submitFeedback(callId, organizationId, feedbackInput);

      if (result.success) {
        setHasSubmittedFeedback(true);
        console.log("✅ [Feedback] Feedback submitted successfully");
      } else {
        console.error("❌ [Feedback] Failed to submit:", result.error);
        const isNetworkError = result.error
          ? result.error.toLowerCase().includes("failed to fetch") ||
            result.error.toLowerCase().includes("network error")
          : false;
        const errorMessage = isNetworkError
          ? NETWORK_ERROR_MESSAGE
          : result.error;
        setSubmitError(errorMessage || "Failed to submit feedback. Please try again.");
      }
    } catch (error) {
      console.error("❌ [Feedback] Error submitting feedback:", error);
      if (error instanceof TypeError && error.message === "Failed to fetch") {
        setSubmitError(NETWORK_ERROR_MESSAGE);
        return;
      }
      if (error instanceof Error && error.message.toLowerCase().includes("network error")) {
        setSubmitError(NETWORK_ERROR_MESSAGE);
        return;
      }
      setSubmitError("An error occurred while submitting feedback.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Manual fetch function for testing
  const handleManualFetch = async () => {
    if (!testCallId.trim()) {
      setViolationsError("Please enter a Call ID");
      return;
    }

    try {
      setIsLoadingViolations(true);
      setViolationsError(null);

      const timelineResponse = await TimelineEventService.getTimelineEvents(testCallId.trim(), 'violations');
      
      
      // First deduplicate the timeline events
      const deduplicatedEvents = deduplicateTimelineEvents(timelineResponse.timeline_events);
      
      // Calculate actual violations by filtering deduplicated timeline events
      const actualViolationCount = deduplicatedEvents.filter(event => {
        // Window blur: only 'blur' action is a violation
        if (event.type === 'window_blur') {
          return event.action === 'blur';
        }
        // Tab change: only 'hidden' action is a violation
        if (event.type === 'tab_change') {
          return event.action === 'hidden';
        }
        // Window manipulation: 'resize', 'maximize', and 'restore' are violations
        if (event.type === 'window_manipulation') {
          return event.action === 'resize' || event.action === 'maximize' || event.action === 'restore' || event.action === 'violation' || event.action === 'warning' || event.action === 'penalty';
        }
        // Screen share violations: always count as violations
        if (event.type === 'screen_share_violation') {
          return event.action === 'violation' || event.action === 'warning' || event.action === 'penalty';
        }
        // For other types, consider violation/warning/penalty actions as violations
        return event.action === 'violation' || event.action === 'warning' || event.action === 'penalty';
      }).length;

      setInterviewData(prev => ({
        ...prev,
        detailedViolations: {
          ...timelineResponse,
          timeline_events: deduplicatedEvents // Use deduplicated events
        },
        // Use the corrected violation count
        violationsCount: actualViolationCount,
      }));
      
    } catch (error) {
      console.error('[InterviewComplete] Manual fetch error:', error);
      setViolationsError(`Manual fetch failed: ${error}`);
    } finally {
      setIsLoadingViolations(false);
    }
  };

  // Helper function to get dynamic content based on end reason
  const getContentDetails = () => {
    const isViolationBased = [
      VIDEO_CALL_END_REASONS.TAB_CHANGE_VIOLATION,
      VIDEO_CALL_END_REASONS.WINDOW_BLUR_VIOLATION,
      VIDEO_CALL_END_REASONS.MONITOR_VIOLATION,
      VIDEO_CALL_END_REASONS.CAMERA_VIOLATION,
      VIDEO_CALL_END_REASONS.SCREENSHARE_VIOLATION,
      VIDEO_CALL_END_REASONS.FACE_DETECTION_VIOLATION,
      VIDEO_CALL_END_REASONS.TOTAL_VIOLATION,
      VIDEO_CALL_END_REASONS.WINDOW_MANIPULATION_VIOLATION,
      VIDEO_CALL_END_REASONS.FULLSCREEN_EXIT_VIOLATION,
    ].includes(interviewData.endReason as any);

    const isNetworkIssue = [
      VIDEO_CALL_END_REASONS.NETWORK_DISCONNECTED,
      VIDEO_CALL_END_REASONS.NETWORK_CONNECTION_LOST,
      VIDEO_CALL_END_REASONS.CONNECTION_FAILED,
      VIDEO_CALL_END_REASONS.TECHNICAL_ISSUES,
      VIDEO_CALL_END_REASONS.SERVER_SHUTDOWN,
    ].includes(interviewData.endReason as any);

    if (isNetworkIssue) {
      console.log('🔍 [InterviewComplete] Network issue detected:', {
        endReason: interviewData.endReason,
        networkIssueReasons: [
          VIDEO_CALL_END_REASONS.NETWORK_DISCONNECTED,
          VIDEO_CALL_END_REASONS.NETWORK_CONNECTION_LOST,
          VIDEO_CALL_END_REASONS.CONNECTION_FAILED,
          VIDEO_CALL_END_REASONS.TECHNICAL_ISSUES,
          VIDEO_CALL_END_REASONS.SERVER_SHUTDOWN,
        ]
      });

      return {
        icon: (
          <img
            src="/network-issue-icon.svg"
            alt="Network issue"
            className="w-16 h-16"
            onError={(e) => {
              // Fallback to a default icon if network-issue-icon.svg doesn't exist
              e.currentTarget.src = "/interview-terminated-icon.svg";
            }}
          />
        ),
        title: "Interview Ended Due to Network Issue",
        subtitle: "The interview session was interrupted due to a network connectivity problem. This was not due to any violations on your part.",
        showViolations: false,
        nextSteps: {
          title: "What's Next?",
          description: "Please contact the recruitment team to reschedule your interview. They will provide you with a new interview link.",
        },
        supportText: (
          <span className="text-lg font-bold">
            For immediate assistance, contact{" "}
            <a
              href="mailto:support@hirevox.ai"
              className="text-[#8437bb] underline hover:text-[#6b2e94] text-lg"
            >
              support@hirevox.ai
            </a>
          </span>
        ),
      };
    }

    if (isViolationBased) {
      return {
        icon: (
          <img
            src="/interview-terminated-icon.svg"
            alt="Interview terminated"
            className="w-16 h-16"
          />
        ),
        title: "Interview Terminated",
        subtitle: `Because Hirevox Detected ${interviewData.violationsCount} Violations During the Interview!`,
        showViolations: true,
        nextSteps: {
          title: "You May Need to Re-Take The Interview!",
          description: "Please connect with the concerned recruiter for further assistance.",
        },
        supportText: (
          <span className="text-lg font-bold">
            You can also write your concerns at{" "}
            <a 
              href="mailto:support@hirevox.ai" 
              className="text-[#8437bb] underline hover:text-[#6b2e94] text-lg"
            >
              support@hirevox.ai
            </a>
          </span>
        ),
      };
    }

    // Default successful completion
    return {
      icon: (
        <img
          src="https://storage.googleapis.com/tempo-image-previews/figma-exports%2Fgithub%7C146932725-1752825941448-node-3260%3A45582-1752825940614.png"
          alt="Success checkmark"
          className="w-16 h-16"
        />
      ),
      title: `Congratulations ${interviewData.userName}!`,
      subtitle: "You Have Successfully Completed Your AI Technical Interview with HireVox",
      showViolations: false,
      nextSteps: {
        title: "What's Next?",
        description: "The recruitment team will review your responses and get back to you in a few days.",
      },
      supportText: null,
    };
  };

  // Helper function to get violation display details
  const getViolationDetails = () => {
    const details = [];
    
    // If we have detailed violations from timeline events, use those
    if (interviewData.detailedViolations?.timeline_events) {
      const timelineEvents = interviewData.detailedViolations.timeline_events;
      
      // Count actual violations by filtering events
      const windowBlurViolations = timelineEvents.filter(event => 
        event.type === 'window_blur' && event.action === 'blur'
      ).length;
      
      const tabChangeViolations = timelineEvents.filter(event => 
        event.type === 'tab_change' && event.action === 'hidden'
      ).length;
      
      // Map timeline event types to UI elements
      if (windowBlurViolations > 0) {
        details.push({ 
          icon: <Monitor className="w-5 h-5 text-danger" />, 
          label: `Window Focus\nLost (${windowBlurViolations})`,
          count: windowBlurViolations
        });
      }
      
      if (tabChangeViolations > 0) {
        details.push({ 
          icon: <AlertTriangle className="w-5 h-5 text-danger" />, 
          label: `Tab Switch\nDetected (${tabChangeViolations})`,
          count: tabChangeViolations
        });
      }
      
      // Count other violation types
      const screenShareViolations = timelineEvents.filter(event => 
        (event.type === 'screen_share_validation' || event.type === 'screen_share_violation') && 
        (event.action === 'violation' || event.action === 'warning' || event.action === 'penalty')
      ).length;
      
      const cameraViolations = timelineEvents.filter(event => 
        event.type === 'camera_violation' && (event.action === 'violation' || event.action === 'warning' || event.action === 'penalty')
      ).length;
      
      const monitorDetectionViolations = timelineEvents.filter(event => 
        event.type === 'monitor_detection' && (event.action === 'violation' || event.action === 'warning' || event.action === 'penalty')
      ).length;
      
      const faceDetectionViolations = timelineEvents.filter(event => 
        event.type === 'face_detection' && (event.action === 'violation' || event.action === 'warning' || event.action === 'penalty')
      ).length;
      
      const fullscreenExitViolations = timelineEvents.filter(event => 
        event.type === 'fullscreen_exit' && (event.action === 'violation' || event.action === 'warning' || event.action === 'penalty')
      ).length;
      
      const windowManipulationViolations = timelineEvents.filter(event => 
        event.type === 'window_manipulation' && (event.action === 'resize' || event.action === 'maximize' || event.action === 'restore' || event.action === 'violation' || event.action === 'warning' || event.action === 'penalty')
      ).length;
      
      if (windowManipulationViolations > 0) {
        details.push({ 
          icon: <Monitor className="w-6 h-6 text-danger" />, 
          label: `Window Changes (${windowManipulationViolations})`,
          count: windowManipulationViolations
        });
      }
      
      if (screenShareViolations > 0) {
        details.push({ 
          icon: <Share className="w-5 h-5 text-danger" />, 
          label: `Screen Sharing Stopped`,
          count: screenShareViolations
        });
      }
      
      if (cameraViolations > 0) {
        details.push({ 
          icon: <Camera className="w-6 h-6 text-danger" />, 
          label: `Camera Issues (${cameraViolations})`,
          count: cameraViolations
        });
      }
      
      if (monitorDetectionViolations > 0) {
        details.push({ 
          icon: <Monitor className="w-6 h-6 text-danger" />, 
          label: `External Monitor Detected`,
          count: monitorDetectionViolations
        });
      }
      
      if (faceDetectionViolations > 0) {
        details.push({ 
          icon: <Camera className="w-6 h-6 text-danger" />, 
          label: `Face Detection\nIssues (${faceDetectionViolations})`,
          count: faceDetectionViolations
        });
      }
      
      if (fullscreenExitViolations > 0) {
        details.push({ 
          icon: <AlertTriangle className="w-6 h-6 text-danger" />, 
          label: `Fullscreen Exit (${fullscreenExitViolations})`,
          count: fullscreenExitViolations
        });
      }
      
      // Add any other violation types we might have missed
      const knownTypes = ['window_blur', 'tab_change', 'screen_share_validation', 'screen_share_violation', 'camera_violation', 'monitor_detection', 'face_detection', 'fullscreen_exit', 'window_manipulation'];
      const otherViolationTypes = {};
      
      timelineEvents.forEach(event => {
        if (!knownTypes.includes(event.type) && (event.action === 'violation' || event.action === 'warning' || event.action === 'penalty')) {
          otherViolationTypes[event.type] = (otherViolationTypes[event.type] || 0) + 1;
        }
      });
      
      Object.entries(otherViolationTypes).forEach(([type, count]) => {
        if ((count as number) > 0) {
          details.push({ 
            icon: <AlertTriangle className="w-6 h-6 text-danger" />, 
            label: `${type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())} (${count})`,
            count: count as number
          });
        }
      });
    } else {
      // Fallback to original logic if no detailed violations available
      if (interviewData.endReason === VIDEO_CALL_END_REASONS.WINDOW_BLUR_VIOLATION) {
        details.push({ icon: <Monitor className="w-5 h-5 text-danger" />, label: "Window Focus\nLost" });
      }
      
      if (interviewData.endReason === VIDEO_CALL_END_REASONS.TAB_CHANGE_VIOLATION) {
        details.push({ icon: <AlertTriangle className="w-5 h-5 text-danger" />, label: "Tab Switch\nDetected" });
      }
      
      if (interviewData.endReason === VIDEO_CALL_END_REASONS.SCREENSHARE_VIOLATION) {
        details.push({ icon: <Share className="w-5 h-5 text-danger" />, label: "Screen Share\nDisconnected" });
      }
      
      if (interviewData.endReason === VIDEO_CALL_END_REASONS.CAMERA_VIOLATION) {
        details.push({ icon: <Camera className="w-6 h-6 text-danger" />, label: "Camera Disconnected" });
      }
      
      if (interviewData.endReason === VIDEO_CALL_END_REASONS.MONITOR_VIOLATION) {
        details.push({ icon: <Monitor className="w-6 h-6 text-danger" />, label: "External Monitor Detected" });
      }

      // If no specific violations, show generic violations
      if (details.length === 0 && interviewData.violationsCount > 0) {
        details.push({ icon: <AlertTriangle className="w-6 h-6 text-danger" />, label: "Violations Detected" });
      }
    }

    return details;
  };

  const contentDetails = getContentDetails();
  const violationDetails = getViolationDetails();

  return (
    <div className="force-light w-full min-h-screen bg-gradient-to-tr from-[#f2e9f8] to-white flex flex-col" style={{ fontFamily: 'Satoshi, sans-serif' }}>
      {/* Header */}
      <div className="w-full px-6 py-4 bg-card border-b border-border">
        <div className="flex items-center gap-2">
          <img
            src="/hirevox.svg"
            alt="HireVox Logo"
            className="h-10"
          />
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col justify-center items-center px-6 py-10">
        <div className="w-full max-w-4xl flex flex-col justify-center items-center gap-8">
          {/* Dynamic Icon and Title */}
          <div className="flex flex-col justify-center items-center gap-4">
            <div className="w-16 h-16 flex justify-center items-center">
              {contentDetails.icon}
            </div>
            <div className="flex flex-col justify-center items-center gap-1">
              <h1 className="text-center text-foreground text-[30px] font-semibold font-general-sans leading-tight">
                {contentDetails.title}
              </h1>
              <p className="w-full max-w-[691px] text-center text-foreground text-[17px] font-normal font-general-sans">
                {contentDetails.subtitle}
              </p>
            </div>
          </div>

          {/* Dynamic Stats Cards */}
            <div className="w-fit p-3 bg-card rounded-xl border border-[#e7d7f3] flex justify-center items-center gap-3">
              {contentDetails.showViolations && violationDetails.length > 0 ? (
              // Show specific violations when terminated due to violations
              violationDetails.map((violation, index) => (
                <div
                  key={index}
                  className="w-[160px] h-[120px] py-4 rounded-xl flex flex-col justify-center items-center gap-2 flex-shrink-0"
                  style={{ backgroundColor: "rgba(236, 64, 64, 0.2)" }} // #EC4040 at 20%
                >
                  <div
                    className="w-8 h-8 flex justify-center items-center rounded-full"
                    style={{ backgroundColor: "#EC4040" }}
                  >
                    {/* Clone the icon and force white color */}
                    {React.cloneElement(violation.icon, { className: "w-5 h-5 text-white" })}
                  </div>
                  <div className="flex flex-col justify-center items-center w-full">
                    <div className="text-[#000000] text-l font-bold font-general-sans leading-tight text-center whitespace-pre-line overflow-hidden text-ellipsis w-full max-w-[120px]">
                      {violation.label}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              // Show normal stats for successful completion
              <>
                {/* Total Time Card */}
                <div className="w-[160px] h-[120px] py-3 bg-[#e8d8f3] rounded-xl flex flex-col justify-center items-center gap-2.5 flex-shrink-0">
                  <div className="w-6 h-6 flex justify-center items-center">
                    <Timer className="w-6 h-6 text-[#8437bb]" />
                  </div>
                  <div className="flex flex-col justify-center items-center">
                    <div className="text-[#8437bb] text-sm font-normal font-general-sans leading-[18px]">
                      Total Time
                    </div>
                    <div className="text-[#8437bb] text-xl font-medium font-general-sans leading-relaxed">
                      {formatTotalTime(interviewData.totalTime)}
                    </div>
                  </div>
                </div>

                {/* Violations Card */}
                <div className="w-[160px] h-[120px] py-3 bg-[#e8d8f3] rounded-xl flex flex-col justify-center items-center gap-2.5 flex-shrink-0">
                  <div className="w-6 h-6 flex justify-center items-center">
                    <CircleAlert className="w-6 h-6 text-[#8437bb]" />
                  </div>
                  <div className="flex flex-col justify-center items-center">
                    <div className="text-[#8437bb] text-sm font-normal font-general-sans leading-[18px]">
                      Violations Detected
                    </div>
                    <div className="text-[#8437bb] text-xl font-medium font-general-sans leading-relaxed">
                      {interviewData.violationsCount}
                    </div>
                  </div>
                </div>
              </>
            )}
            </div>

            {interviewData.detailedViolations?.call_metadata?.career_url && (
              <div className="-mt-5 max-w-[300px] w-full flex justify-center items-center">
                <a href={interviewData.detailedViolations.call_metadata.career_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-center text-muted-foreground text-[15px] font-normal font-general-sans leading-tight hover:text-foreground">
                  Check other openings <ArrowUpRight className="w-3 h-3" />
                </a>
              </div>
              )}

          {/* Loading State for Detailed Violations */}
          {isLoadingViolations && (
            <div className="w-full max-w-[550px] p-4 bg-card rounded-xl border border-[#e7d7f3] flex justify-center items-center gap-3">
              <div className="flex items-center gap-2 text-muted-foreground">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#8437bb]"></div>
                <span className="text-sm">Loading detailed violation data...</span>
              </div>
            </div>
          )}

          {/* Error State for Detailed Violations */}
          {violationsError && (
            <div className="w-full max-w-[550px] p-4 bg-card rounded-xl border border-[#e7d7f3] flex justify-center items-center gap-3">
              <div className="flex items-center gap-2 text-danger">
                <AlertTriangle className="w-4 h-4" />
                <span className="text-sm">{violationsError}</span>
              </div>
            </div>
          )}

          {/* Manual Testing Section */}
          {/* <div className="w-full max-w-[550px] bg-secondary rounded-xl border border-border p-4">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-2 h-2 bg-primary rounded-full"></div>
              <h3 className="text-foreground text-lg font-semibold font-general-sans">
                Manual Timeline Fetch (Testing)
              </h3>
            </div>
            
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Call ID:
                </label>
                <input
                  type="text"
                  value={testCallId}
                  onChange={(e) => setTestCallId(e.target.value)}
                  placeholder="Enter call ID to test..."
                  className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                />
              </div>
              
              <button
                onClick={handleManualFetch}
                disabled={isLoadingViolations}
                className="w-full bg-primary hover:bg-primary disabled:bg-blue-400 text-primary-foreground px-4 py-2 rounded-md font-medium text-sm transition-colors"
              >
                {isLoadingViolations ? "Fetching..." : "Fetch Timeline Events"}
              </button>
            </div>
          </div> */}

          {/* Detailed Timeline Events Section - COMMENTED OUT */}
          {/*
          {interviewData.detailedViolations && interviewData.detailedViolations.timeline_events.length > 0 && (
            <div className="w-full max-w-[550px] bg-card rounded-xl border border-[#e7d7f3] p-4">
              <div className="flex items-center gap-2 mb-4">
                <Timer className="w-5 h-5 text-[#8437bb]" />
                <h3 className="text-[#8437bb] text-lg font-semibold font-general-sans">
                  Timeline Events ({interviewData.detailedViolations.timeline_events.length})
                </h3>
              </div>
              
              <div className="max-h-48 overflow-y-auto space-y-2">
                {interviewData.detailedViolations.timeline_events
                  .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
                  .map((event, index) => {
                    const eventTime = new Date(event.timestamp).toLocaleTimeString();
                    const eventType = event.type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
                    const isViolation = (() => {
                      // Window blur: only 'blur' action is a violation
                      if (event.type === 'window_blur') {
                        return event.action === 'blur';
                      }
                      // Tab change: only 'hidden' action is a violation
                      if (event.type === 'tab_change') {
                        return event.action === 'hidden';
                      }
                      // Window manipulation: 'resize', 'maximize', and 'restore' are violations
                      if (event.type === 'window_manipulation') {
                        return event.action === 'resize' || event.action === 'maximize' || event.action === 'restore' || event.action === 'violation' || event.action === 'warning' || event.action === 'penalty';
                      }
                      // Screen share violations: always count as violations
                      if (event.type === 'screen_share_violation') {
                        return event.action === 'violation' || event.action === 'warning' || event.action === 'penalty';
                      }
                      // For other types, consider violation/warning/penalty actions as violations
                      return event.action === 'violation' || event.action === 'warning' || event.action === 'penalty';
                    })();
                    
                    return (
                      <div 
                        key={index}
                        className={`flex items-center gap-3 p-2 rounded-lg ${
                          isViolation ? 'bg-danger-bg border border-danger-line' : 'bg-muted border border-border'
                        }`}
                      >
                        <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                          isViolation ? 'bg-destructive' : 'bg-muted'
                        }`} />
                        <div className="flex-1 min-w-0">
                          <div className={`text-sm font-medium ${
                            isViolation ? 'text-danger' : 'text-foreground'
                          }`}>
                            {eventType} - {event.action}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {eventTime}
                            {event.data?.duration && ` • ${event.data.duration}s`}
                            {event.data?.violationCount && ` • Count: ${event.data.violationCount}`}
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </div>
              
              <div className="mt-4 pt-3 border-t border-border">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="text-center">
                    <div className="text-[#8437bb] font-semibold">
                      {interviewData.detailedViolations.violation_summary.timeline_stats.total_events}
                    </div>
                    <div className="text-muted-foreground">Total Events</div>
                  </div>
                  <div className="text-center">
                    <div className="text-danger font-semibold">
                      {interviewData.detailedViolations.violation_summary.timeline_stats.violation_events}
                    </div>
                    <div className="text-muted-foreground">Violations</div>
                  </div>
                </div>
              </div>
            </div>
          )}
          */}

          {/* Dynamic Next Steps Section */}
          <div className="flex flex-col justify-center items-center gap-1">
            <div className="text-foreground text-[17px] font-bold font-general-sans leading-tight">
              {contentDetails.nextSteps.title}
            </div>
            <div className="text-center text-muted-foreground text-[15px] font-normal font-general-sans leading-tight">
              {contentDetails.nextSteps.description}
            </div>
            {contentDetails.supportText && (
              <div className="text-center text-foreground text-[15px] font-normal font-general-sans leading-tight mt-2">
                {contentDetails.supportText}
              </div>
            )}
          </div>

          {/* Feedback Section */}
          {!hasSubmittedFeedback ? (
            <div id="feedback-section" className="w-full max-w-[800px] bg-card rounded-xl border border-[#e7d7f3] p-8 shadow-sm">
              <div className="flex flex-col gap-6">
                {/* Header */}
                <div className="flex flex-col gap-1">
                  <h2 className="text-foreground text-[21px] font-bold font-general-sans leading-tight">
                    Please Share Your Feedback
                  </h2>
                  <p className="text-muted-foreground text-[15px] font-normal font-general-sans">
                    Help Us Improve Your Interview Experience
                  </p>
                </div>

                {/* Star Rating */}
                <div className="flex flex-col gap-3">
                  <label className="text-foreground text-[15px] font-medium font-general-sans">
                    Rate Your Overall Experience
                  </label>
                  <StarRating
                    value={rating}
                    onChange={(value) => {
                      setRating(value);
                      if (value > 3) {
                        setFeedbackTextError(null);
                      }
                    }}
                  />
                </div>

                {/* Text Feedback */}
                <div className="flex flex-col gap-3">
                  <label className="text-foreground text-[15px] font-medium font-general-sans">
                    Feedback or Suggestions {isFeedbackTextRequired ? "(Required)" : "(Optional)"}
                  </label>
                  <textarea
                    value={feedbackText}
                    onChange={(e) => {
                      setFeedbackText(e.target.value);
                      if (feedbackTextError) setFeedbackTextError(null);
                      if (submitError) setSubmitError(null);
                    }}
                    onBlur={() => {
                      if (isFeedbackTextRequired && !feedbackText.trim()) {
                        setFeedbackTextError(FEEDBACK_REQUIRED_MESSAGE);
                      }
                    }}
                    placeholder={
                      isFeedbackTextRequired
                        ? "Please tell us what could be improved"
                        : "Please share any feedback or suggestions you may have"
                    }
                    rows={3}
                    className={`w-full px-4 py-3 bg-card border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#8437bb] focus:border-transparent resize-none text-foreground text-[15px] font-general-sans placeholder:text-ink-faint ${
                      feedbackTextError ? "border-danger-line" : "border-border"
                    }`}
                  />
                  {feedbackTextError && (
                    <p className="text-sm text-danger font-medium">
                      {feedbackTextError}
                    </p>
                  )}
                </div>

                {/* Error Message */}
                {submitError && (
                  <div className="p-4 bg-danger-bg border border-danger-line rounded-xl flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-danger flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-danger text-sm font-medium">
                        {submitError}
                      </p>
                      <button
                        onClick={handleSubmitFeedback}
                        disabled={rating === 0 || isSubmitting}
                        className="mt-2 text-danger underline text-sm hover:text-danger"
                      >
                        Try again
                      </button>
                    </div>
                  </div>
                )}

                {/* Submit Button */}
                <div className="flex justify-end">
                    <button
                    onClick={handleSubmitFeedback}
                    disabled={rating === 0 || isSubmitting}
                    className={`
                      px-8 py-3 rounded-xl font-semibold text-[17px] font-general-sans
                      transition-all duration-200
                      min-w-[220px]
                      ${
                      rating === 0
                        ? "bg-muted text-muted-foreground cursor-not-allowed"
                        : "bg-[#8437bb] text-white hover:bg-[#6b2e94] active:scale-95"
                      }
                      ${isSubmitting ? "opacity-70 cursor-wait" : ""}
                    `}
                    >
                    {isSubmitting ? "Submitting..." : "Submit"}
                    </button>
                </div>

                {/* Validation Message */}
                {/* {rating === 0 && !submitError && (
                  <p className="text-sm text-muted-foreground text-center italic">
                    Please rate your experience before submitting
                  </p>
                )} */}
              </div>
            </div>
          ) : (
            /* Success Message */
            <div className="w-full max-w-[800px] bg-card rounded-xl border border-[#e7d7f3] p-8 shadow-sm">
              <div className="flex flex-col items-center gap-4">
                <div className="w-16 h-16 bg-good-bg rounded-full flex items-center justify-center">
                  <CheckCircle className="w-8 h-8 text-good" />
                </div>
                <div className="text-center">
                  <h3 className="text-foreground text-[21px] font-bold font-general-sans mb-2">
                    Thank you for your feedback!
                  </h3>
                  <p className="text-muted-foreground text-[15px] font-general-sans">
                    Your input helps us improve the interview experience for everyone.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      {showExitWarning && !hasSubmittedFeedback && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4 backdrop-blur-sm">
          <div
            className="w-full max-w-md rounded-2xl bg-card p-8 shadow-2xl animate-scale-in"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="exit-warning-title"
            aria-describedby="exit-warning-description"
          >
            <div className="flex flex-col items-center gap-5 text-center">
              {/* Icon */}
              <div className="w-16 h-16 rounded-full bg-[#8437bb]/10 flex items-center justify-center">
                <AlertTriangle className="h-8 w-8 text-[#8437bb]" />
              </div>

              {/* Title */}
              <div className="space-y-0">
                <h3 id="exit-warning-title" className="text-xl font-bold text-foreground font-general-sans">
                  Feedback Required Before Leaving
                </h3>
                <p id="exit-warning-description" className="text-sm text-muted-foreground font-general-sans">
                  Please take a moment to rate your interview experience. Your feedback is valuable and helps us improve.
                </p>
              </div>

                {/* Rating reminder */}
                {rating === 0 && (
                  <div className="w-full rounded-lg">
                    <p className="text-sm text-mid font-medium">
                      ⭐ You haven't rated your experience yet
                    </p>
                  </div>
                )}

                {/* Actions */}
                <div className="flex flex-col gap-2 w-full">
                <button
                  onClick={() => {
                  setShowExitWarning(false);
                  // Scroll to feedback section
                  document.getElementById('feedback-section')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  }}
                  className="w-full h-12 rounded-xl bg-[#8437bb] px-6 py-3 text-white font-semibold transition-colors hover:bg-[#6b2e94] focus:outline-none focus:ring-2 focus:ring-[#8437bb]/40"
                >
                  {rating === 0 ? "Submit Rating" : "Submit Feedback"}
                </button>
                {/* <button
                  onClick={() => setShowExitWarning(false)}
                  className="w-full h-12 rounded-xl bg-muted px-6 py-3 text-foreground font-medium transition-colors hover:bg-muted"
                >
                  I'll do it later
                </button> */}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
